import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loader2, LogOut, Trash2, Upload, FileText, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "myhoom_admin_token";

const CITIES = ["", "Bengaluru", "Dubai", "Global"];
const CATS = ["Market Report", "Investment Thesis", "Micro-market", "Regulatory", "Whitepaper"];

const Login = ({ onLogin }) => {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch {
      setErr("Invalid password");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="card-lift border border-black/5 p-10 w-full max-w-md">
        <span className="eyebrow">Admin</span>
        <h1 className="mt-2 font-serif text-3xl text-navy">My Hoom · Admin</h1>
        <p className="mt-2 text-sm text-navy/60">Sign in to manage research and view enquiries.</p>
        <label className="block mt-7">
          <span className="text-xs text-navy/65">Admin password</span>
          <input
            data-testid="admin-password-input"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="mt-1.5 w-full rounded-xl px-4 py-3 text-sm border border-black/10 outline-none focus:ring-2 focus:ring-gold/50"
            placeholder="••••••••"
            autoFocus
          />
        </label>
        {err && <div data-testid="admin-login-error" className="mt-3 text-sm text-red-500">{err}</div>}
        <button disabled={busy} data-testid="admin-login-submit" className="btn-navy mt-6 w-full">
          {busy ? <Loader2 className="animate-spin" size={16} /> : null}
          Sign in
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("research"); // research | enquiries
  const [docs, setDocs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [form, setForm] = useState({ title: "", summary: "", city: "", category: "Market Report", file: null });
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/research`);
      setDocs(r.data);
    } catch { setDocs([]); }
    try {
      const e = await axios.get(`${API}/contact`, auth);
      setEnquiries(e.data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.title.trim()) { setMsg("Title and file are required"); return; }
    setUploading(true); setMsg("");
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("summary", form.summary);
    fd.append("city", form.city);
    fd.append("category", form.category);
    fd.append("file", form.file);
    try {
      await axios.post(`${API}/research`, fd, { ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setForm({ title: "", summary: "", city: "", category: "Market Report", file: null });
      setMsg("Uploaded");
      document.getElementById("admin-file-input").value = "";
      load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this document from public listing?")) return;
    try { await axios.delete(`${API}/research/${id}`, auth); load(); } catch (e) { alert("Failed"); }
  };

  return (
    <div className="container-tight py-14">
      <div className="flex items-center justify-between gap-4 mb-10">
        <div>
          <span className="eyebrow">Admin</span>
          <h1 className="mt-2 font-serif text-4xl text-navy">My Hoom Dashboard</h1>
        </div>
        <button onClick={onLogout} data-testid="admin-logout" className="btn-ghost-navy text-sm">
          <LogOut size={14}/> Sign out
        </button>
      </div>

      <div className="flex gap-1 border-b border-black/10 mb-8">
        {[{ k: "research", l: "Research Library" }, { k: "enquiries", l: `Enquiries (${enquiries.length})` }].map(t => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-gold text-navy" : "border-transparent text-navy/55 hover:text-navy"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "research" && (
        <div className="grid lg:grid-cols-12 gap-10">
          <form onSubmit={upload} data-testid="admin-upload-form" className="lg:col-span-5 card-lift p-7 border border-black/5 h-fit">
            <h3 className="font-serif text-2xl text-navy flex items-center gap-2"><Upload size={18} /> Upload research</h3>
            <div className="mt-5 space-y-4">
              <input data-testid="upload-title" required placeholder="Title*" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full rounded-xl px-4 py-3 text-sm border border-black/10" />
              <textarea data-testid="upload-summary" rows={3} placeholder="One-line summary" value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} className="w-full rounded-xl px-4 py-3 text-sm border border-black/10 resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="upload-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full rounded-xl px-3 py-3 text-sm border border-black/10">
                  {CITIES.map(c => <option key={c} value={c}>{c || "City (optional)"}</option>)}
                </select>
                <select data-testid="upload-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full rounded-xl px-3 py-3 text-sm border border-black/10">
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <input
                id="admin-file-input"
                data-testid="upload-file"
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.pptx,.png,.jpg,.jpeg"
                onChange={e => setForm({ ...form, file: e.target.files?.[0] })}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-navy file:text-white file:text-xs hover:file:bg-navy/90"
              />
              {msg && <div data-testid="upload-message" className="text-xs text-navy/70">{msg}</div>}
              <button disabled={uploading} data-testid="upload-submit" className="btn-gold w-full text-sm disabled:opacity-60">
                {uploading ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
                {uploading ? "Uploading..." : "Upload document"}
              </button>
              <p className="text-[11px] text-navy/45">Max 25MB · PDF, DOCX, XLSX, PPTX, PNG, JPG</p>
            </div>
          </form>

          <div className="lg:col-span-7 space-y-3">
            <div className="text-sm text-navy/60">{docs.length} document(s) in library</div>
            {docs.length === 0 ? (
              <div className="border border-dashed border-black/10 rounded-2xl p-10 text-center text-navy/50 text-sm">
                <FileText size={24} className="mx-auto text-gold mb-3" /> No documents uploaded yet.
              </div>
            ) : docs.map(d => (
              <div key={d.id} data-testid={`admin-doc-${d.id}`} className="card-lift p-5 border border-black/5 flex items-center gap-4">
                <FileText className="text-gold shrink-0" size={20} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-navy truncate">{d.title}</div>
                  <div className="text-xs text-navy/55 mt-1 truncate">{d.category} · {d.city || "—"} · {d.original_filename}</div>
                </div>
                <a href={`${API}/research/${d.id}/download`} target="_blank" rel="noopener noreferrer" className="text-xs text-navy/70 underline">View</a>
                <button onClick={() => del(d.id)} data-testid={`admin-delete-${d.id}`} className="text-red-500/80 hover:text-red-600 p-2"><Trash2 size={16}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="space-y-3">
          {enquiries.length === 0 ? (
            <div className="border border-dashed border-black/10 rounded-2xl p-10 text-center text-navy/50 text-sm">
              <Inbox size={24} className="mx-auto text-gold mb-3" /> No enquiries yet.
            </div>
          ) : enquiries.map(e => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="card-lift p-6 border border-black/5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-serif text-xl text-navy">{e.name}</div>
                <div className="text-xs text-navy/50">{new Date(e.created_at).toLocaleString()}</div>
              </div>
              <div className="mt-1 text-sm text-navy/65 flex flex-wrap gap-x-4 gap-y-1">
                <span>{e.email}</span><span>{e.phone}</span>
                {e.city && <span className="text-gold">{e.city}</span>}
                {e.service && <span>· {e.service}</span>}
              </div>
              {e.message && <p className="mt-3 text-sm text-navy/75 whitespace-pre-line">{e.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Verify token validity on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
  }, [token]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  return (
    <>
      <Helmet><title>Admin · My Hoom</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </>
  );
};

export default Admin;
